import { useCallback, useMemo } from 'react';

import useUrlParams from './useUrlParams';

const NO_ALIASES = Object.freeze({});
const NO_CLEARS = Object.freeze({});

/**
 * THE `?tab=` reader (KPR-1579): `[activeKey, setTab]` for a page whose tabs
 * live in the address — the card page (components/cards/cardTabs), a
 * collection's settings (CollectionForm), the admin notifications section.
 * One convention, so a pasted `?tab=` means the same thing on every page:
 *
 * - `keys` are the tabs THIS viewer has (a reader is offered fewer than a
 *   manager). A `?tab=` naming any other, or none, reads as `defaultKey`, so
 *   a stale or hand-edited link lands on the default rather than on nothing.
 *   When `keys` grow (access arrives), the address is read again.
 * - The default tab is never written: choosing it DELETES the param, so the
 *   bare route is the canonical address.
 * - Every change is `replace: true`: tabs do not stack up in the Back button.
 * - `aliases` maps an old key to its current one (`{ 'quick-add': 'add-item' }`).
 * - `clearOnChange` names the params that belong to a tab and are dropped on a
 *   change, each with the tabs it is KEPT on: `{ runsRule: ['automations'],
 *   runsPage: [] }` keeps `runsRule` only when the new tab is Automations and
 *   always drops `runsPage`. An array is shorthand for "kept on none".
 * - `param` is the address's name for it, `tab` by default.
 *
 * Built on hooks/useUrlParams, so `setTab` keeps one identity while the
 * address changes (it changes only with `defaultKey` and what it clears).
 */
export default function useTabParam(keys = [], defaultKey, { param = 'tab', aliases = NO_ALIASES, clearOnChange = NO_CLEARS } = {}) {
	const { params, update } = useUrlParams({ tab: { default: defaultKey, values: keys, aliases, param, trim: false } });

	const clears = useMemo(() => (Array.isArray(clearOnChange)
		? clearOnChange.map(name => [name, []])
		: Object.entries(clearOnChange || {})), [JSON.stringify(clearOnChange)]); // eslint-disable-line react-hooks/exhaustive-deps

	const setTab = useCallback((key) => {
		const patch = { tab: key && key !== defaultKey ? key : undefined };
		clears.forEach(([name, keptOn]) => {
			if (!(Array.isArray(keptOn) && keptOn.includes(key))) patch[name] = undefined;
		});
		update(patch);
	}, [update, defaultKey, clears]);

	return [params.tab, setTab];
}
